// src/auth/client.ts
import { COGNITO, COGNITO_ENDPOINTS } from "./config";
import { createCodeVerifierAndChallenge } from "./pkce";
import { readTokens, saveTokens, clearTokens, isExpired, TokenSet } from "./store";
export { decodeJwtPayload } from "../lib/jwt";

const VERIFIER_KEY = "tj.pkce.verifier";
const STATE_KEY = "tj.oauth.state";

function putVerifier(v: string) {
  sessionStorage.setItem(VERIFIER_KEY, v);
}
function takeVerifier(): string | null {
  const v = sessionStorage.getItem(VERIFIER_KEY);
  if (v) sessionStorage.removeItem(VERIFIER_KEY);
  return v;
}
function putState(s: string) {
  sessionStorage.setItem(STATE_KEY, s);
}
function takeState(): string | null {
  const s = sessionStorage.getItem(STATE_KEY);
  if (s) sessionStorage.removeItem(STATE_KEY);
  return s;
}

export async function login() {
  const { verifier, challenge } = await createCodeVerifierAndChallenge();
  putVerifier(verifier);

  const state = crypto.randomUUID();
  putState(state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: COGNITO.clientId,
    redirect_uri: COGNITO.redirectUri,
    scope: COGNITO.scope,
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
  });

  window.location.assign(`${COGNITO_ENDPOINTS.authorize}?${params.toString()}`);
}

export async function handleAuthCallback(): Promise<void> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const error = url.searchParams.get("error");
  if (error) throw new Error(error);

  const expectedState = takeState();
  if (!state || !expectedState || state !== expectedState) {
    throw new Error("STATE_MISMATCH");
  }
  if (!code) throw new Error("MISSING_CODE");

  const verifier = takeVerifier();
  if (!verifier) throw new Error("MISSING_CODE_VERIFIER");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: COGNITO.clientId,
    code,
    redirect_uri: COGNITO.redirectUri,
    code_verifier: verifier,
  });

  const resp = await fetch(COGNITO_ENDPOINTS.token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error_description || "TOKEN_EXCHANGE_FAILED");

  const expires_in = Number(data.expires_in || 3600);
  const tokens: TokenSet = {
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
  };
  saveTokens(tokens);
}

/** Call Cognito token endpoint with refresh_token grant */
async function executeRefresh(refreshToken: string, currentIdToken: string): Promise<TokenSet | null> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: COGNITO.clientId,
    refresh_token: refreshToken,
  });

  const resp = await fetch(COGNITO_ENDPOINTS.token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = await resp.json();
  if (!resp.ok) {
    clearTokens();
    return null;
  }

  const expires_in = Number(data.expires_in || 3600);
  const next: TokenSet = {
    access_token: data.access_token,
    id_token: data.id_token || currentIdToken, // sometimes omitted on refresh
    refresh_token: refreshToken,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
  };
  saveTokens(next);
  return next;
}

export async function refreshTokensIfNeeded(): Promise<TokenSet | null> {
  const current = readTokens();
  if (current && !isExpired(current)) return current;
  if (!current?.refresh_token) return null;
  return executeRefresh(current.refresh_token, current.id_token);
}

/** Force refresh even if the local token hasn't expired yet (e.g. server rejected it).
 *  Concurrent calls are deduplicated to a single in-flight request. */
let _inflightForceRefresh: Promise<TokenSet | null> | null = null;

export function forceRefreshTokens(): Promise<TokenSet | null> {
  if (_inflightForceRefresh) return _inflightForceRefresh;
  const current = readTokens();
  if (!current?.refresh_token) return Promise.resolve(null);
  _inflightForceRefresh = executeRefresh(current.refresh_token, current.id_token)
    .finally(() => { _inflightForceRefresh = null; });
  return _inflightForceRefresh;
}

export function getAccessTokenSync(): string | null {
  const t = readTokens();
  if (!t || isExpired(t)) return null;
  return t.access_token;
}

export async function getValidAccessToken(): Promise<string | null> {
  const t = await refreshTokensIfNeeded();
  return t?.access_token ?? null;
}

export function logout() {
  const tokens = readTokens();
  const idToken = tokens?.id_token;
  const refreshToken = tokens?.refresh_token;

  // Revoke the refresh token server-side so it cannot be reused.
  // This is fire-and-forget: we proceed with local cleanup and redirect
  // regardless of whether the revocation call succeeds.
  if (refreshToken) {
    const revokeUrl = `https://${COGNITO.domain}/oauth2/revoke`;
    const body = new URLSearchParams({
      token: refreshToken,
      client_id: COGNITO.clientId,
    });
    fetch(revokeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    }).catch(() => {
      // Best effort — token will still be invalidated by the Cognito logout redirect
    });
  }

  clearTokens();
  const params = new URLSearchParams({
    client_id: COGNITO.clientId,
    logout_uri: COGNITO.logoutRedirectUri,
  });
  if (idToken) params.set("id_token_hint", idToken);
  window.location.assign(`${COGNITO_ENDPOINTS.logout}?${params.toString()}`);
}

